<script setup lang="ts">
import type {
  IProductCardTrackerData,
  TProductCardAddToCartFn,
} from '@/features/product-card/models';
import { type IProductEntity, ProductMM } from '@/entities';

import { computed, ref } from 'vue';
import ProductCardBase from './ProductCardBase.vue';
import { useIntersectionObserver } from '@vueuse/core';
import { useQuery } from '@tanstack/vue-query';
import { getProductPage } from '@/shared/apollo-api/api/product-page';
import { useCart, useCityStore } from '@/features/store';
import { showSkuSelectPopup } from '@/features/sku-select-popup';
import { gqlPdpProductMapper } from '#shared/mappers/gqlPdpProductMapper';
import { useProductCardTracker } from '../tracker/useProductCardTracker';
import { TRACKER_VIEW_THRESHOLD } from '~/features/clickstream-sdk';
import { snackBarManager } from '~/features/ui-kit';

const props = withDefaults(
  defineProps<{
    addToCartFn?: TProductCardAddToCartFn;
    product: IProductEntity;
    variant?: 'slim' | 'default';
    trackerProductClick?: IProductCardTrackerData;
    trackerProductView?: IProductCardTrackerData;
    updateLinkForAd?: any;
  }>(),
  {
    variant: 'default',
  }
);

const { addProductToCartLite } = useCart();
const addToCartFn = props.addToCartFn ?? addProductToCartLite;

const { trackProductCartClick, trackProductCartView } = useProductCardTracker();

const emit = defineEmits<{
  'favorite-toggle': [product: IProductEntity];
}>();

const productCardBaseRef = ref<InstanceType<typeof ProductCardBase>>();
const isLoading = ref(false);

const { currentCityId } = useCityStore();

const localIsFavorite = ref(props.product.isFavorite || false);

const productWithLocalState = computed(() => ({
  ...props.product,
  isFavorite: localIsFavorite.value,
}));

// GraphQL запрос через TanStack Query
const {
  data: pdp,
  isLoading: productPageGQLLoading,
  refetch,
} = useQuery({
  queryKey: ['product-page', props.product.id, 'ProductCard'],
  queryFn: () =>
    getProductPage(Number(props.product.id), currentCityId.value).then(({ data }) => {
      return gqlPdpProductMapper(data.product);
    }),
  enabled: false, // Запрос не выполняется автоматически
});
// Обработчик добавления в корзину
const handleAddToCart = async (product: IProductEntity) => {
  isLoading.value = true;
  // Получаем полную информацию о продукте при добавлении в корзину
  if (!pdp.value) {
    await refetch();
  }

  // Проверяем информацию о SKU для добавления в корзину
  if (pdp.value?.skuList) {
    const skuCount = pdp.value.skuList.length;
    const totalAvailableAmount = pdp.value.skuList.reduce(
      (sum: number, sku: any) => sum + (sku.availableAmount || 0),
      0
    );

    if (totalAvailableAmount <= 0) {
      // TODO Заменить после релиза на popup
      snackBarManager.showSnackBar({
        type: 'error',
        message: 'Этот товар уже раскупили',
        dismissible: true,
      });

      isLoading.value = false;
      return;
    }

    if (skuCount > 1) {
      // Get plid from multiple sources (priority order):
      // 1. trackerProductClick.additionalPayload.plid
      // 2. trackerProductClick.additionalPayload.module_id (used in main-offer.vue)
      // 3. product.category.id
      const plid =
        props.trackerProductClick?.additionalPayload?.plid ||
        props.trackerProductClick?.additionalPayload?.module_id ||
        props.product.category?.id ||
        undefined;

      // Get referer from document.referrer
      const referer =
        typeof window !== 'undefined' && document.referrer ? document.referrer : undefined;

      showSkuSelectPopup({
        pdp: pdp.value,
        addToCartFn,
        plid,
        referer,
      });
    } else {
      const [sku] = pdp.value.skuList;

      // TODO: Добавить в корзину
      await addToCartFn({ ...product, skuId: sku.skuId });

      // TODO Заменить после релиза на popup
      snackBarManager.showSnackBar({
        type: 'success',
        message: 'Товар добавлен в корзину',
        dismissible: true,
      });
    }

    isLoading.value = false;
  }
};

const handleLike = async (product: IProductEntity) => {
  const previousState = localIsFavorite.value;
  localIsFavorite.value = !localIsFavorite.value;

  emit('favorite-toggle', { ...product, isFavorite: previousState });
};

const router = useRouter();
function onClick(product: IProductEntity, isMetaOrCtrlPressed?: boolean) {
  if (props.trackerProductClick?.enabled) {
    trackProductCartClick(props.product, props.trackerProductClick.additionalPayload);
  }

  let url = ProductMM.getLink(props.product);
  if (!url) return;
  // фикс ссылки для 11.11
  if(props.updateLinkForAd) {
    url = props.updateLinkForAd(url)
  }
  isMetaOrCtrlPressed ? window.open(url, '_blank') : router.push(url);
}

/** трекаем только если надо */
if (props.trackerProductView?.enabled) {
  const { stop: productCardIntersectionObserverStop } = useIntersectionObserver(
    () => productCardBaseRef.value?.productCardEl,
    ([{ isIntersecting }]) => {
      if (!isIntersecting) return;

      trackProductCartView(props.product, props.trackerProductView.additionalPayload);
      productCardIntersectionObserverStop();
    },
    { threshold: TRACKER_VIEW_THRESHOLD }
  );
}
</script>

<template>
  <ProductCardBase
    ref="productCardBaseRef"
    :product="productWithLocalState"
    :variant="variant"
    :loading="isLoading"
    @add-to-cart="handleAddToCart"
    @like="handleLike"
    @click="onClick"
  />
</template>
